import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { FileFormat } from 'app/shared/enums';
import { downloadBlobAs, objectToCSV } from 'app/shared/functions';

@Component({
  selector: 'app-parcel-table',
  templateUrl: './parcel-table.component.html',
  styleUrls: ['./parcel-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParcelTableComponent {
  @Input() isLoading = true;
  @Input() set source(value: any[]) {
    this.rowsDataSource = new MatTableDataSource([...value] ?? []);
    this.buildColumns(value);
  }
  @Output() close = new EventEmitter();
  @Output() hoverRow = new EventEmitter();

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.rowsDataSource.sort = sort;
  }

  rowsDataSource = new MatTableDataSource([]);
  columns: { name: string; label: string; selected: boolean; hidden: boolean }[] = [];
  displayedColumns = [] as string[];

  private lastHoveredRow = null;

  getLabel(column: string): string {
    return this.columns.find((x) => x.name === column)?.label ?? column;
  }

  getValue(row: any, column: string): string {
    const [level1, level2] = column.split('.');
    return row[level1]?.[level2];
  }

  onSaveColumns(columns: any[]): void {
    this.displayedColumns = columns.map((x) => x.name);
    localStorage.setItem('parcelColumns', JSON.stringify(this.displayedColumns));
  }

  export(): void {
    const dataToExport = this.rowsDataSource.data.map((row) =>
      this.displayedColumns.reduce((p, c) => ({ ...p, [this.getLabel(c)]: this.getValue(row, c) }), {}),
    );

    const [first] = dataToExport;
    const columns = Object.keys(first);
    const plainCsv = objectToCSV(dataToExport, columns);
    const fileName = `Parcel-Export-${new Date().getTime()}`;

    downloadBlobAs(fileName, FileFormat.CSV, ['\ufeff' + plainCsv]);
  }

  onHoverRow(row: any): void {
    if (this.lastHoveredRow?.parcel?.id !== row.parcel?.id) {
      this.lastHoveredRow = row;
      this.hoverRow.emit(row);
    }
  }

  sortChange(event: Sort): void {
    this.rowsDataSource.data.sort((a: any, b: any) => this.sortByProp(a, b, event.active));
    if (event.direction === 'desc') {
      this.rowsDataSource.data.reverse();
    }
  }

  private buildColumns(value: any[]): void {
    if (!value?.length) {
      this.columns = [];
      this.displayedColumns = [];
      return;
    }

    const [firstValue] = value;
    const parcelColumns = Object.keys(firstValue.parcel)
      .map((s) => ({
        name: `parcel.${s}`,
        label: this.toLabel(s),
        hidden: ['id', 'srcId', 'apn2'].includes(s),
      }))
      .map((x) => ({ ...x, selected: !x.hidden }));

    const storedColumns = localStorage.getItem('parcelColumns');

    if (!storedColumns) {
      this.displayedColumns = parcelColumns.filter((x) => !x.hidden).map((s) => s.name);
      localStorage.setItem('parcelColumns', JSON.stringify(this.displayedColumns));
    } else {
      this.displayedColumns = JSON.parse(storedColumns);
    }

    this.columns = [
      ...parcelColumns,
      ...Object.keys(firstValue.taxAssessor).map((s) => ({
        name: `taxAssessor.${s}`,
        label: this.toLabel(s),
        hidden: false,
        selected: false,
      })),
    ].map((column) => ({ ...column, selected: this.displayedColumns.includes(column.name) }));
  }

  private toLabel(value: string): string {
    return new TitleCasePipe().transform(value.replace(/([A-Z])/g, ' $1').trim());
  }

  private sortByProp(a: any, b: any, prop: string): number {
    const [level1, level2] = prop.split('.');
    if (a[level1]?.[level2] < b[level1]?.[level2]) return -1;
    if (a[level1]?.[level2] > b[level1]?.[level2]) return 1;
    return 0;
  }
}
